import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useWallet } from "@/context/WalletContext";
import { useCurrency } from "@/context/CurrencyContext";
import {
  Wallet,
  Plus,
  Minus,
  ArrowDownToLine,
  ArrowUpFromLine,
  Loader2,
  RefreshCw,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const STXOSHI_CONTRACT = "SP1Z92MPDQEWZXW36VX71Q25HKF5K2EPCJ304F275.tokensoft-token-v4karmaedcr";

/**
 * UserWallet — the shared site-wallet card used across Flip Market and Perps.
 *
 * Displays the user's STXOSHI balance in big bold green and offers Deposit /
 * Withdraw actions with the same on-chain flow used by Flip Market.
 *
 * Props:
 *   - onBalanceChange(balance):  Optional callback fired whenever the balance
 *                                updates so a parent page can react (e.g. gate
 *                                its Make Predictions / Open Position button).
 *   - refreshKey:                Optional number — bump to force a re-fetch
 *                                (useful after a trade closes on the parent).
 */
export default function UserWallet({ onBalanceChange, refreshKey }) {
  const { isConnected, getAuthHeaders, addresses } = useWallet();
  const { showUsd, stxoshiUsd } = useCurrency();
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [depositAmount, setDepositAmount] = useState("");
  const [pendingDeposit, setPendingDeposit] = useState(null);
  const [checkingOnChain, setCheckingOnChain] = useState(false);

  const fetchWallet = useCallback(async () => {
    if (!isConnected) return;
    try {
      const r = await fetch(`${API}/api/flip-market/player-wallet`, { headers: getAuthHeaders() });
      const d = await r.json();
      if (d?.success) {
        const b = d.data.balance_stxoshi || 0;
        setBalance(b);
        onBalanceChange?.(b);
      }
    } catch (e) {
      console.error("[UserWallet] fetch failed:", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  useEffect(() => {
    fetchWallet();
  }, [fetchWallet, refreshKey]);

  // Keep the card fresh in the background so the balance reflects trades
  // opened/closed on the parent page or other tabs.
  useEffect(() => {
    if (!isConnected) return;
    const t = setInterval(fetchWallet, 5000);
    return () => clearInterval(t);
  }, [isConnected, fetchWallet]);

  async function handleDeposit() {
    const amount = parseFloat(depositAmount) * 1_000_000;
    if (!amount || amount < 1_000_000) {
      toast.error("Minimum deposit is 1 STXOSHI");
      return;
    }
    setLoading(true);
    try {
      const initR = await fetch(`${API}/api/flip-market/player-wallet/initiate-deposit`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ amount_stxoshi: amount }),
      });
      const initD = await initR.json();
      if (!initD.success) throw new Error(initD.detail || "Failed to initiate deposit");
      const { deposit_id, recipient_address } = initD;

      toast.info("Please sign the STXOSHI transfer in your wallet…");
      const satsConnect = await import("sats-connect");
      const stacksTx = await import("@stacks/transactions");

      const postCondition = stacksTx.Pc.principal(addresses.stx)
        .willSendEq(BigInt(amount))
        .ft(STXOSHI_CONTRACT, "tokensoft-token");

      const payload = {
        contract: STXOSHI_CONTRACT,
        functionName: "transfer",
        arguments: [
          stacksTx.cvToHex(stacksTx.uintCV(amount)),
          stacksTx.cvToHex(stacksTx.principalCV(addresses.stx)),
          stacksTx.cvToHex(stacksTx.principalCV(recipient_address)),
          stacksTx.cvToHex(stacksTx.noneCV()),
        ],
        postConditions: [stacksTx.postConditionToHex(postCondition)],
        postConditionMode: "deny",
      };

      const response = await satsConnect.request("stx_callContract", payload);
      if (!response?.result?.txid) throw new Error("Transaction cancelled or failed");
      const txId = response.result.txid;

      setPendingDeposit({ deposit_id, tx_id: txId, amount });

      const confirmR = await fetch(`${API}/api/flip-market/player-wallet/confirm-deposit`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ deposit_id, tx_id: txId }),
      });
      const confirmD = await confirmR.json();
      if (confirmD.success) {
        setBalance(confirmD.data.new_balance);
        onBalanceChange?.(confirmD.data.new_balance);
        setShowDepositModal(false);
        setDepositAmount("");
        setPendingDeposit(null);
        toast.success(`Deposited ${amount / 1_000_000} STXOSHI to your User Wallet!`);
      } else {
        toast.error("Deposit signed but confirmation failed. Use \"Check On-Chain\" button.");
      }
    } catch (e) {
      console.error("Deposit error:", e);
      toast.error(e.message || "Deposit failed");
    } finally {
      setLoading(false);
    }
  }

  async function handleCheckOnChain() {
    if (!pendingDeposit) return;
    setCheckingOnChain(true);
    try {
      const txStatusR = await fetch(`https://api.hiro.so/extended/v1/tx/${pendingDeposit.tx_id}`);
      const txStatus = await txStatusR.json();
      if (txStatus.tx_status === "success") {
        const confirmR = await fetch(`${API}/api/flip-market/player-wallet/confirm-deposit`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          body: JSON.stringify({ deposit_id: pendingDeposit.deposit_id, tx_id: pendingDeposit.tx_id }),
        });
        const confirmD = await confirmR.json();
        if (confirmD.success) {
          setBalance(confirmD.data.new_balance);
          onBalanceChange?.(confirmD.data.new_balance);
          setShowDepositModal(false);
          setDepositAmount("");
          setPendingDeposit(null);
          toast.success(`Deposit verified! ${pendingDeposit.amount / 1_000_000} STXOSHI credited!`);
        } else {
          toast.info(
            <div>
              TX confirmed on-chain but not credited.{" "}
              <a
                href={`https://explorer.hiro.so/txid/${pendingDeposit.tx_id}?chain=mainnet`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-400 underline ml-1"
              >
                View TX
              </a>
            </div>
          );
        }
      } else if (txStatus.tx_status === "pending") {
        toast.info("Transaction still pending on blockchain. Please wait a few minutes.");
      } else {
        toast.error(`Transaction ${txStatus.tx_status}. No funds were deducted.`);
        setPendingDeposit(null);
      }
    } catch (e) {
      console.error("On-chain check error:", e);
      toast.error("Failed to check on-chain status. Try again.");
    } finally {
      setCheckingOnChain(false);
    }
  }

  async function handleWithdraw() {
    if (balance <= 0) {
      toast.error("Nothing to withdraw");
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/flip-market/player-wallet/withdraw`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({}),
      });
      const d = await r.json();
      if (d.success) {
        setBalance(d.data.new_balance);
        onBalanceChange?.(d.data.new_balance);
        setShowWithdrawModal(false);
        toast.success(`Withdrew ${d.data.withdrawn_amount / 1_000_000} STXOSHI!`);
      } else {
        toast.error(d.detail || "Withdrawal failed");
      }
    } catch (e) {
      toast.error(e.message || "Withdrawal failed");
    } finally {
      setLoading(false);
    }
  }

  if (!isConnected) return null;

  const balanceStxoshi = (balance || 0) / 1_000_000;
  const isPositive = balance > 0;
  const balanceDisplay = showUsd
    ? `$${(balanceStxoshi * (stxoshiUsd || 0.0000037)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : balanceStxoshi.toLocaleString(undefined, { maximumFractionDigits: 2 });

  return (
    <>
      <Card
        className={`glass-card ${isPositive ? "border-green-500/50" : "border-orange-500/50"}`}
        data-testid="user-wallet-card"
      >
        <CardContent className="p-2 sm:p-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <Wallet className="w-4 h-4 text-orange-400 flex-shrink-0" />
              <span
                className={`text-lg font-mono font-bold ${isPositive ? "text-green-400" : "text-foreground"}`}
                data-testid="user-wallet-balance"
              >
                {balanceDisplay}
              </span>
            </div>
            <div className="flex gap-1.5 flex-shrink-0">
              <Button
                size="sm"
                onClick={() => setShowDepositModal(true)}
                disabled={loading}
                className="h-7 px-2 text-xs bg-green-600 hover:bg-green-700"
                data-testid="user-wallet-deposit-btn"
              >
                <Plus className="w-3 h-3 mr-0.5" /> Deposit
              </Button>
              <Button
                size="sm"
                onClick={() => setShowWithdrawModal(true)}
                disabled={loading || balance <= 0}
                variant="outline"
                className="h-7 px-2 text-xs border-orange-500 text-orange-400 hover:bg-orange-500/10"
                data-testid="user-wallet-withdraw-btn"
              >
                <Minus className="w-3 h-3 mr-0.5" /> Withdraw
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Deposit Modal */}
      {showDepositModal && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => !loading && !checkingOnChain && setShowDepositModal(false)}
          data-testid="user-wallet-deposit-modal"
        >
          <div
            className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
              <ArrowDownToLine className="w-5 h-5 text-green-400" />
              Add Funds to User Wallet
            </h3>
            <p className="text-zinc-400 text-sm mb-4">Deposit STXOSHI from your Xverse wallet</p>

            <div className="space-y-4">
              <input
                type="number"
                placeholder="Amount (STXOSHI)"
                value={depositAmount}
                onChange={(e) => setDepositAmount(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-muted/30 border border-border font-mono"
              />

              <div className="flex gap-2">
                {[10, 50, 100, 500].map((amount) => (
                  <button
                    key={amount}
                    onClick={() => setDepositAmount(amount.toString())}
                    className="flex-1 py-2 rounded-lg bg-muted/50 hover:bg-muted text-sm font-mono"
                  >
                    {amount}
                  </button>
                ))}
              </div>

              <Button
                onClick={handleDeposit}
                disabled={loading || !depositAmount || parseFloat(depositAmount) < 1}
                className="w-full h-12 bg-green-600 hover:bg-green-700"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : `Deposit ${depositAmount || "0"} STXOSHI`}
              </Button>

              {pendingDeposit && (
                <button
                  onClick={handleCheckOnChain}
                  disabled={checkingOnChain}
                  data-testid="check-onchain-btn"
                  className="w-full py-3 rounded-lg font-semibold text-white
                    bg-gradient-to-r from-cyan-500 to-blue-600
                    hover:from-cyan-400 hover:to-blue-500
                    animate-pulse shadow-lg shadow-cyan-500/30
                    disabled:opacity-50 disabled:animate-none
                    flex items-center justify-center gap-2"
                >
                  {checkingOnChain ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Checking blockchain…
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      Check On-Chain (if stuck on this page)
                    </>
                  )}
                </button>
              )}

              <Button
                variant="ghost"
                onClick={() => setShowDepositModal(false)}
                disabled={loading || checkingOnChain}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Withdraw Modal */}
      {showWithdrawModal && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => !loading && setShowWithdrawModal(false)}
          data-testid="user-wallet-withdraw-modal"
        >
          <div
            className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
              <ArrowUpFromLine className="w-5 h-5 text-orange-400" />
              Withdraw from User Wallet
            </h3>

            <div className="p-4 rounded-xl bg-muted/30 border border-border mb-4">
              <p className="text-sm text-muted-foreground">Available Balance</p>
              <p className="text-2xl font-mono font-bold text-green-400">
                {(balance / 1_000_000).toLocaleString()} STXOSHI
              </p>
            </div>

            <Button
              onClick={handleWithdraw}
              disabled={loading || balance <= 0}
              className="w-full h-12 bg-orange-600 hover:bg-orange-700 mb-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Withdraw All"}
            </Button>

            <Button
              variant="ghost"
              onClick={() => setShowWithdrawModal(false)}
              disabled={loading}
              className="w-full"
            >
              Cancel
            </Button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
